import { AgendaItemStatusEnum } from "enums/AgendaItemStatusEnum";
import { AgendaItemTypeEnum } from "enums/AgendaItemTypeEnum";
import { createLogger } from "logging";
import { AgendaItemModel } from "models/AgendaItemModel";
import { PatchAgendaItemModel } from "models/PatchAgendaItemModel";
import { createContext } from "react";
import { UseFormReturn } from "react-hook-form";

const logger = createLogger("EditAgendaItemContext");

export interface AgendaItemFieldValues {
  title: string;
  duration: number;
  status: AgendaItemStatusEnum;
  type: AgendaItemTypeEnum;
  // Note: 'confidential' is not present in AgendaItemModel (read), but exists in PatchAgendaItemModel (write).
  // Add it to AgendaItemModel once the API returns it.
  confidential: boolean;
  locked: boolean;
  department: string;
  note: string;
  guests: string;
  finalBoard: string;
  decision: string;
  perusal: string;
  protocolEntryDraft: string;
  protocolEntry: string;
}

export function getAgendaItemFieldValues(agendaItem: AgendaItemModel): AgendaItemFieldValues {
  const title = agendaItem.title || "";
  const duration = agendaItem.duration || 0;
  const status = agendaItem.isDeleted ? AgendaItemStatusEnum.Deleted : agendaItem.status;
  const type = agendaItem.type ?? AgendaItemTypeEnum.Decision;
  const confidential = agendaItem.confidential ?? false;
  const locked = agendaItem.locked || false;
  const department = agendaItem.department || "";
  const note = agendaItem.note || "";
  const guests = agendaItem.guests || "";
  const finalBoard = agendaItem.finalBoard || "";
  const decision = agendaItem.decision || "";
  const perusal = agendaItem.perusal || "";
  const protocolEntryDraft = agendaItem.protocolEntryDraft || "";
  const protocolEntry = agendaItem.protocolEntry || "";

  const values = {
    title,
    duration,
    status,
    type,
    confidential,
    locked,
    department,
    note,
    guests,
    finalBoard,
    decision,
    perusal,
    protocolEntryDraft,
    protocolEntry,
  };

  logger.debug(`getAgendaItemFieldValues(): `, values);
  return values;
}

export interface EditAgendaItemContextProps {
  confirmUnsaved: boolean;
  setConfirmUnsaved: (newState: boolean) => void;
  editAgendaItemId: number;
  setEditAgendaItemId: (id: number) => void;
  isLoading: boolean;
  isDirty: boolean;
  isValid: boolean;
  isSubmittable: boolean;
  isSubmitting: boolean;
  form: UseFormReturn<AgendaItemFieldValues>;
  getPatch: (values: AgendaItemFieldValues) => PatchAgendaItemModel;
  handleSave: () => Promise<void>;
  handleClose: (ignoreDirty?: boolean) => void;
}

export const EditAgendaItemContext = createContext({} as EditAgendaItemContextProps);
